import { z } from "zod";
export const accountLifecyclePolicyVersionSchema = z.literal("phase10-deletion-decision-lock.v1");
export const accountLifecyclePolicyEnvironmentSchema = z.literal("staging");
export const accountLifecyclePolicyStatusSchema = z.enum(["locally_locked", "launch_gated"]);
export const accountLifecycleLaunchGateSchema = z.enum([
    "production_legal_approval",
    "production_product_approval",
    "production_operations_owner",
    "support_mailbox_qualification",
    "security_key_management_review",
    "better_auth_secret_rotation_review",
    "phase10_7_schema_migration",
    "staging_qualification",
]);
const boundedIdSchema = z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9](?:[a-z0-9_:-]*[a-z0-9])?$/);
const boundedTextSchema = z.string().trim().min(1).max(1000);
const boundedLongTextSchema = z.string().trim().min(1).max(4000);
const tableNameSchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z][a-z0-9_]*$/);
const columnNameSchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z][a-z0-9_]*$/);
const artifactMetadataBaseSchema = z.strictObject({
    schema: z.literal("tutorlite.account-lifecycle-decision-lock/v1"),
    artifact: boundedIdSchema,
    version: accountLifecyclePolicyVersionSchema,
    environment: accountLifecyclePolicyEnvironmentSchema,
    status: accountLifecyclePolicyStatusSchema,
    approved: z.literal(false),
    publishable: z.literal(false),
    technicalDecisionLock: z.literal(true),
    notProductionLegalApproval: z.literal(true),
    gates: z.array(accountLifecycleLaunchGateSchema).min(1).max(10),
});
function artifactMetadataSchema(artifact) {
    return artifactMetadataBaseSchema.extend({ artifact: z.literal(artifact) });
}
export const retentionActionSchema = z.enum([
    "delete_at_execution",
    "delete_after_expiry",
    "redact_after_expiry",
    "retain_under_surrogate",
    "retain_no_identity",
]);
export const retentionCategorySchema = z.strictObject({
    id: boundedIdSchema,
    label: boundedTextSchema,
    trigger: boundedTextSchema,
    durationDays: z.number().int().min(1).max(3650).nullable(),
    action: retentionActionSchema,
    retainedFields: z.array(boundedIdSchema).max(30),
    owner: boundedIdSchema,
    rationale: boundedLongTextSchema,
    productionApprovalRequired: z.boolean(),
    currentRuntimeConfigDiscrepancy: z.string().trim().min(1).max(300).optional(),
});
export const retentionPolicySchema = artifactMetadataSchema("retention_policy")
    .extend({ categories: z.array(retentionCategorySchema).min(1).max(30) })
    .superRefine(validateRetentionPolicy);
export const transformActionSchema = z.enum([
    "delete",
    "retain_no_identity",
    "replace_with_surrogate",
    "anonymize",
    "retain_live_until_execution",
    "not_user_owned",
    "phase10_7_add_column_required",
]);
export const transformIdentityRoleSchema = z.enum([
    "subject_user",
    "subject_owner",
    "guardian",
    "enrollment",
    "payer",
    "beneficiary",
    "requester",
    "provider_evidence",
    "support_contact",
    "admin_actor",
    "audit_target",
    "login_credential",
    "session_credential",
    "oauth_flow",
    "proof_credential",
    "moderation_subject",
    "learning_profile",
    "none",
]);
export const deletionTransformRowSchema = z
    .strictObject({
    table: tableNameSchema,
    columns: z.array(columnNameSchema).min(1).max(20),
    action: transformActionSchema,
    identityRole: transformIdentityRoleSchema,
    retentionCategory: boundedIdSchema.optional(),
    phase10_7AddColumnRequired: z.boolean(),
    notes: boundedLongTextSchema,
})
    .superRefine((row, context) => {
    if (row.action === "phase10_7_add_column_required" && !row.phase10_7AddColumnRequired) {
        context.addIssue({
            code: "custom",
            path: ["phase10_7AddColumnRequired"],
            message: "Phase 10.7 column requirements must be marked explicitly",
        });
    }
    if (row.action !== "not_user_owned" && row.identityRole === "none") {
        context.addIssue({
            code: "custom",
            path: ["identityRole"],
            message: "User-owned transform rows require an identity role",
        });
    }
});
export const deletionTransformMatrixSchema = artifactMetadataSchema("deletion_transform_matrix")
    .extend({ rows: z.array(deletionTransformRowSchema).min(1).max(260) })
    .superRefine((matrix, context) => {
    const seen = new Set();
    for (const [rowIndex, row] of matrix.rows.entries()) {
        for (const column of row.columns) {
            const key = `${row.table}.${column}`;
            if (seen.has(key)) {
                context.addIssue({
                    code: "custom",
                    path: ["rows", rowIndex, "columns"],
                    message: `Duplicate transform coverage for ${key}`,
                });
            }
            seen.add(key);
        }
    }
});
export const accountDeletionStateSchema = z.enum([
    "not_requested",
    "cooling_off",
    "cancelled",
    "processing",
    "retryable_error",
    "completed",
]);
export const accountDeletionTransitionSchema = z.strictObject({
    from: accountDeletionStateSchema,
    to: accountDeletionStateSchema,
    trigger: boundedIdSchema,
    effects: z.array(boundedTextSchema).min(1).max(12),
    requiredBlockersClear: z.array(boundedIdSchema).max(20),
});
export const accountDeletionStateMachineSchema = artifactMetadataSchema("account_deletion_state_machine")
    .extend({
    initialState: z.literal("not_requested"),
    terminalStates: z.tuple([z.literal("cancelled"), z.literal("completed")]),
    transitions: z.array(accountDeletionTransitionSchema).min(1).max(30),
})
    .superRefine(validateStateMachine);
export const latePaymentEventSchema = z.enum(["checkout", "payment", "refund", "dispute"]);
export const deletionLifecycleStateSchema = z.enum(["none", "cooling_off", "completed"]);
export const latePaymentOutcomeSchema = z.enum([
    "normal_processing",
    "persist_and_freeze_effect",
    "manual_refund_review",
    "unfulfilled_manual_refund_support",
    "block_execution_until_resolved",
]);
const statusListSchema = z.array(z.string().min(1).max(80)).min(1).max(20);
export const latePaymentDecisionRowSchema = z.strictObject({
    caseId: boundedIdSchema,
    event: latePaymentEventSchema,
    deletionState: deletionLifecycleStateSchema,
    checkoutStatuses: statusListSchema.optional(),
    paymentStatuses: statusListSchema.optional(),
    refundStatuses: statusListSchema.optional(),
    disputeStatuses: statusListSchema.optional(),
    outcome: latePaymentOutcomeSchema,
    freezesValue: z.boolean(),
    blocksExecution: z.boolean(),
    restoresProfile: z.literal(false),
    notes: boundedLongTextSchema,
});
export const latePaymentDecisionTableSchema = artifactMetadataSchema("late_payment_decision_table")
    .extend({ rows: z.array(latePaymentDecisionRowSchema).min(1).max(80) })
    .superRefine(validateLatePaymentDecisionTable);
export const legalHoldStateSchema = z.enum(["none", "active", "released"]);
export const legalHoldTransitionSchema = z.strictObject({
    from: legalHoldStateSchema,
    to: legalHoldStateSchema,
    trigger: boundedIdSchema,
    effectOnDeletionExecution: z.enum(["none", "block_execution", "allow_execution"]),
    notes: boundedLongTextSchema,
});
export const legalHoldPolicySchema = artifactMetadataSchema("legal_hold_policy")
    .extend({
    states: z.array(legalHoldStateSchema).min(3).max(3),
    transitions: z.array(legalHoldTransitionSchema).min(1).max(10),
})
    .superRefine((policy, context) => {
    const states = new Set(policy.states);
    for (const required of ["none", "active", "released"]) {
        if (!states.has(required)) {
            context.addIssue({ code: "custom", path: ["states"], message: `Missing ${required}` });
        }
    }
    if (!policy.transitions.some((transition) => transition.from === "none" &&
        transition.to === "active" &&
        transition.effectOnDeletionExecution === "block_execution")) {
        context.addIssue({
            code: "custom",
            path: ["transitions"],
            message: "Active legal holds must block deletion execution",
        });
    }
});
export const valueDispositionSchema = z.enum([
    "none",
    "refund_review",
    "forfeiture_selection",
    "manual_review_unresolved",
]);
export const valueDispositionCaseSchema = z.strictObject({
    caseId: boundedIdSchema,
    disposition: valueDispositionSchema,
    requiredSelection: z.array(valueDispositionSchema).max(3),
    freezesValue: z.boolean(),
    blocksExecution: z.boolean(),
    notes: boundedLongTextSchema,
});
export const valueDispositionPolicySchema = artifactMetadataSchema("value_disposition_policy")
    .extend({ cases: z.array(valueDispositionCaseSchema).min(1).max(20) })
    .superRefine(validateValueDispositionPolicy);
export const preflightBlockerCodeSchema = z.enum([
    "unresolved_refund",
    "unresolved_dispute",
    "active_legal_hold",
    "value_disposition_required",
    "active_admin_role",
    "last_superadmin",
    "ownership_transfer_required",
    "guardian_separate_learner_delete_not_allowed",
    "tombstone_key_unavailable",
    "support_ops_launch_gate",
]);
export const preflightBlockerCodeDefinitionSchema = z.strictObject({
    code: preflightBlockerCodeSchema,
    blocksProfileDeactivation: z.boolean(),
    blocksExecution: z.boolean(),
    resolvableBy: z
        .array(z.enum(["user", "admin", "support", "operations", "security"]))
        .min(1)
        .max(5),
    messageKey: boundedIdSchema,
    notes: boundedLongTextSchema,
});
export const preflightBlockerCodesSchema = artifactMetadataSchema("preflight_blocker_codes")
    .extend({ codes: z.array(preflightBlockerCodeDefinitionSchema).min(1).max(30) })
    .superRefine(validatePreflightBlockerCodes);
export const tombstoneIdentityPolicySchema = artifactMetadataSchema("tombstone_identity_policy")
    .extend({
    algorithm: z.literal("HMAC-SHA-256"),
    keyDerivation: z.strictObject({
        algorithm: z.literal("HKDF-SHA-256"),
        sourceSecret: z.literal("BETTER_AUTH_SECRET"),
        salt: z.literal("tutorlite/tombstone-email-key-derivation/salt/v1"),
        infoPrefix: z.literal("tutorlite/tombstone-email-hmac/info/v1/environment/"),
        environmentDomainSeparation: z.literal(true),
        derivedKeyUsage: z.literal("HMAC-SHA-256"),
        maintenanceReceivesSecret: z.literal(false),
        pendingSubjectHashColumn: z.literal("account_deletion_requests.pending_subject_hash"),
    }),
    canonicalizationVersion: boundedIdSchema,
    activeKeyVersion: boundedIdSchema,
    acceptedKeyVersions: z
        .array(z.strictObject({
        keyVersion: boundedIdSchema,
        status: z.enum(["active", "retired"]),
        writesAllowed: z.boolean(),
        matchesAllowed: z.boolean(),
    }))
        .min(1)
        .max(10),
    inputFields: z.tuple([z.literal("normalized_email")]),
    normalizationSteps: z.array(boundedTextSchema).min(1).max(20),
    rotationRule: boundedLongTextSchema,
    betterAuthSecretRotationRequirement: boundedLongTextSchema,
    collisionRule: boundedLongTextSchema,
    restoresOldState: z.literal(false),
})
    .superRefine(validateTombstoneIdentityPolicy);
export const supportResponseTargetSchema = z.strictObject({
    category: z.enum([
        "account_access",
        "billing",
        "privacy",
        "technical",
        "refund_request",
        "deletion_status",
    ]),
    targetHours: z.number().int().min(1).max(720),
    escalationAfterHours: z.number().int().min(1).max(720),
});
export const supportEscalationRouteSchema = z.strictObject({
    category: z.enum([
        "account_access",
        "billing",
        "privacy",
        "technical",
        "refund_request",
        "deletion_status",
    ]),
    primaryOwner: boundedIdSchema,
    fallbackOwner: boundedIdSchema,
});
export const supportOperationsPolicySchema = artifactMetadataSchema("support_operations_policy")
    .extend({
    launchGateStatus: z.literal("launch_gated"),
    monitoredMailboxRequired: z.literal(true),
    helpdeskOrMailboxOwnerRequired: z.literal(true),
    ownerRole: boundedIdSchema,
    mailboxAddressPlaceholder: z.literal("support@<domain>"),
    responseTargets: z.array(supportResponseTargetSchema).min(1).max(12),
    escalationRoutes: z.array(supportEscalationRouteSchema).min(1).max(12),
    productionApprovalRequired: z.literal(true),
    notes: boundedLongTextSchema,
})
    .superRefine(validateSupportOperationsPolicy);
export const previewCopyItemSchema = z.strictObject({
    key: boundedIdSchema,
    title: boundedTextSchema,
    body: boundedLongTextSchema,
});
export const deletionPreviewLocaleCopySchema = z.strictObject({
    locale: z.enum(["id", "en"]),
    title: boundedTextSchema,
    summary: boundedLongTextSchema,
    blockerMessages: z.array(previewCopyItemSchema).min(1).max(40),
    valueDispositionOptions: z.array(previewCopyItemSchema).min(2).max(6),
});
export const bilingualDeletionPreviewCopySchema = artifactMetadataSchema("bilingual_deletion_preview_copy")
    .extend({ locales: z.tuple([deletionPreviewLocaleCopySchema, deletionPreviewLocaleCopySchema]) })
    .superRefine(validatePreviewCopyLocales);
export const accountLifecycleDecisionLockBundleSchema = z
    .strictObject({
    version: accountLifecyclePolicyVersionSchema,
    retentionPolicy: retentionPolicySchema,
    deletionTransformMatrix: deletionTransformMatrixSchema,
    accountDeletionStateMachine: accountDeletionStateMachineSchema,
    latePaymentDecisionTable: latePaymentDecisionTableSchema,
    legalHoldPolicy: legalHoldPolicySchema,
    valueDispositionPolicy: valueDispositionPolicySchema,
    preflightBlockerCodes: preflightBlockerCodesSchema,
    tombstoneIdentityPolicy: tombstoneIdentityPolicySchema,
    supportOperationsPolicy: supportOperationsPolicySchema,
    bilingualDeletionPreviewCopy: bilingualDeletionPreviewCopySchema,
})
    .superRefine(validateDecisionLockBundle);
const REQUIRED_RETENTION_CATEGORIES = [
    "financial_audit",
    "admin_audit",
    "raw_learning_events_free",
    "raw_learning_events_paid",
    "support_closed",
    "expired_challenge_proof",
    "learning_profile",
    "completed_deletion_request",
];
const REQUIRED_LATE_PAYMENT_CASES = [
    "checkout_before_request",
    "checkout_open_during_cooling_off",
    "checkout_terminal_during_cooling_off",
    "checkout_paid_after_completed_deletion",
    "checkout_terminal_after_completed_deletion",
    "payment_pending_during_cooling_off",
    "payment_terminal_no_value_during_cooling_off",
    "payment_value_during_cooling_off",
    "payment_pending_after_completed_deletion",
    "payment_terminal_no_value_after_completed_deletion",
    "payment_value_after_completed_deletion",
    "refund_open_during_cooling_off",
    "refund_terminal_during_cooling_off",
    "dispute_during_cooling_off",
    "refund_open_after_completed_deletion",
    "refund_terminal_after_completed_deletion",
    "dispute_after_completed_deletion",
];
const CHECKOUT_STATUSES = [
    "creating",
    "reconciling",
    "provider_created",
    "pending",
    "paid",
    "paid_capacity_conflict",
    "expired",
    "failed",
    "cancelled",
];
const PAYMENT_STATUSES = [
    "pending",
    "paid",
    "partially_refunded",
    "refunded",
    "disputed",
    "chargeback",
    "failed",
    "expired",
];
const REFUND_STATUSES = [
    "creating",
    "provider_created",
    "pending",
    "succeeded",
    "failed",
    "cancelled",
];
const DISPUTE_STATUSES = ["disputed", "chargeback"];
const REQUIRED_BLOCKER_CODES = [
    "unresolved_refund",
    "unresolved_dispute",
    "active_legal_hold",
    "value_disposition_required",
    "active_admin_role",
    "last_superadmin",
    "ownership_transfer_required",
    "guardian_separate_learner_delete_not_allowed",
    "tombstone_key_unavailable",
    "support_ops_launch_gate",
];
function validateTombstoneIdentityPolicy(policy, context) {
    if (!/^[-a-z0-9_]+-v[0-9]+$/.test(policy.canonicalizationVersion)) {
        context.addIssue({
            code: "custom",
            path: ["canonicalizationVersion"],
            message: "Canonicalization version must include a numeric version suffix",
        });
    }
    const seen = new Set();
    for (const [index, key] of policy.acceptedKeyVersions.entries()) {
        if (seen.has(key.keyVersion)) {
            context.addIssue({
                code: "custom",
                path: ["acceptedKeyVersions", index, "keyVersion"],
                message: "Duplicate tombstone key version",
            });
        }
        seen.add(key.keyVersion);
        if (key.status === "active" && (!key.writesAllowed || !key.matchesAllowed)) {
            context.addIssue({
                code: "custom",
                path: ["acceptedKeyVersions", index],
                message: "Active tombstone key must allow writes and matches",
            });
        }
        if (key.status === "retired" && key.writesAllowed) {
            context.addIssue({
                code: "custom",
                path: ["acceptedKeyVersions", index],
                message: "Retired tombstone keys cannot allow writes",
            });
        }
    }
    const active = policy.acceptedKeyVersions.find((key) => key.keyVersion === policy.activeKeyVersion);
    if (active?.status !== "active") {
        context.addIssue({
            code: "custom",
            path: ["activeKeyVersion"],
            message: "Active key version must exist in the key registry with active status",
        });
    }
    if (!policy.gates.includes("security_key_management_review")) {
        context.addIssue({
            code: "custom",
            path: ["gates"],
            message: "Tombstone policy must remain security-review gated",
        });
    }
    if (!policy.gates.includes("better_auth_secret_rotation_review")) {
        context.addIssue({
            code: "custom",
            path: ["gates"],
            message: "Tombstone policy must gate Better Auth secret rotation",
        });
    }
}
function validateSupportOperationsPolicy(policy, context) {
    for (const gate of ["production_operations_owner", "support_mailbox_qualification"]) {
        if (!policy.gates.includes(gate)) {
            context.addIssue({ code: "custom", path: ["gates"], message: `Missing ${gate}` });
        }
    }
    const targetCategories = new Set(policy.responseTargets.map((target) => target.category));
    const routeCategories = new Set(policy.escalationRoutes.map((route) => route.category));
    for (const category of [
        "account_access",
        "billing",
        "privacy",
        "refund_request",
        "deletion_status",
    ]) {
        if (!targetCategories.has(category) || !routeCategories.has(category)) {
            context.addIssue({
                code: "custom",
                path: ["responseTargets"],
                message: `Support operations policy must cover ${category}`,
            });
        }
    }
    for (const [index, target] of policy.responseTargets.entries()) {
        if (target.escalationAfterHours < target.targetHours) {
            context.addIssue({
                code: "custom",
                path: ["responseTargets", index, "escalationAfterHours"],
                message: "Escalation target must not be shorter than first response target",
            });
        }
    }
}
function validateRetentionPolicy(policy, context) {
    const categories = new Map(policy.categories.map((category) => [category.id, category]));
    for (const required of REQUIRED_RETENTION_CATEGORIES) {
        if (!categories.has(required)) {
            context.addIssue({ code: "custom", path: ["categories"], message: `Missing ${required}` });
        }
    }
    expectDuration(categories, "financial_audit", 2555, context);
    expectDuration(categories, "admin_audit", 2555, context);
    expectDuration(categories, "raw_learning_events_free", 90, context);
    expectDuration(categories, "raw_learning_events_paid", 180, context);
    expectDuration(categories, "support_closed", 730, context);
    expectDuration(categories, "expired_challenge_proof", 1, context);
    const supportClosed = categories.get("support_closed");
    if (supportClosed &&
        (!supportClosed.productionApprovalRequired ||
            supportClosed.currentRuntimeConfigDiscrepancy !== "SUPPORT_RETENTION_DAYS=365")) {
        context.addIssue({
            code: "custom",
            path: ["categories"],
            message: "Support retention discrepancy and legal approval gate must stay explicit",
        });
    }
    const learningProfile = categories.get("learning_profile");
    if (learningProfile && learningProfile.durationDays !== null) {
        context.addIssue({
            code: "custom",
            path: ["categories"],
            message: "Learning/profile data must delete at execution rather than after a day count",
        });
    }
}
function expectDuration(categories, id, durationDays, context) {
    if (categories.get(id)?.durationDays !== durationDays) {
        context.addIssue({
            code: "custom",
            path: ["categories"],
            message: `${id} must use ${durationDays} days`,
        });
    }
}
function validateStateMachine(stateMachine, context) {
    const transitions = new Set(stateMachine.transitions.map((transition) => `${transition.from}->${transition.to}`));
    for (const required of [
        "not_requested->cooling_off",
        "cooling_off->cancelled",
        "cooling_off->processing",
        "processing->retryable_error",
        "retryable_error->processing",
        "processing->completed",
    ]) {
        if (!transitions.has(required)) {
            context.addIssue({ code: "custom", path: ["transitions"], message: `Missing ${required}` });
        }
    }
    const requestTransition = stateMachine.transitions.find((transition) => transition.from === "not_requested" && transition.to === "cooling_off");
    if (!requestTransition?.effects.some((effect) => effect.includes("deactivate owner"))) {
        context.addIssue({
            code: "custom",
            path: ["transitions"],
            message: "Deletion request must deactivate the owner immediately",
        });
    }
}
function validateLatePaymentDecisionTable(table, context) {
    const cases = new Map(table.rows.map((row) => [row.caseId, row]));
    for (const required of REQUIRED_LATE_PAYMENT_CASES) {
        if (!cases.has(required)) {
            context.addIssue({ code: "custom", path: ["rows"], message: `Missing ${required}` });
        }
    }
    requireLateStatusCoverage(table, "checkout", "none", CHECKOUT_STATUSES, context);
    requireLateStatusCoverage(table, "checkout", "cooling_off", CHECKOUT_STATUSES, context);
    requireLateStatusCoverage(table, "checkout", "completed", CHECKOUT_STATUSES, context);
    requireLateStatusCoverage(table, "payment", "cooling_off", PAYMENT_STATUSES, context);
    requireLateStatusCoverage(table, "payment", "completed", PAYMENT_STATUSES, context);
    requireLateStatusCoverage(table, "refund", "cooling_off", REFUND_STATUSES, context);
    requireLateStatusCoverage(table, "refund", "completed", REFUND_STATUSES, context);
    requireLateStatusCoverage(table, "dispute", "cooling_off", DISPUTE_STATUSES, context);
    requireLateStatusCoverage(table, "dispute", "completed", DISPUTE_STATUSES, context);
    const duringCoolingOff = cases.get("payment_value_during_cooling_off");
    if (duringCoolingOff && (!duringCoolingOff.freezesValue || duringCoolingOff.restoresProfile)) {
        context.addIssue({
            code: "custom",
            path: ["rows"],
            message: "Cooling-off payments must persist but freeze effects without profile restore",
        });
    }
    const afterDeletion = cases.get("payment_value_after_completed_deletion");
    if (afterDeletion?.outcome !== "unfulfilled_manual_refund_support") {
        context.addIssue({
            code: "custom",
            path: ["rows"],
            message: "Post-deletion payments must become unfulfilled manual-refund support cases",
        });
    }
}
function requireLateStatusCoverage(table, event, deletionState, statuses, context) {
    for (const status of statuses) {
        const covered = table.rows.some((row) => {
            if (row.event !== event || row.deletionState !== deletionState)
                return false;
            if (event === "checkout")
                return row.checkoutStatuses?.includes(status) ?? false;
            if (event === "payment")
                return row.paymentStatuses?.includes(status) ?? false;
            if (event === "refund")
                return row.refundStatuses?.includes(status) ?? false;
            return row.disputeStatuses?.includes(status) ?? false;
        });
        if (!covered) {
            context.addIssue({
                code: "custom",
                path: ["rows"],
                message: `Missing ${event} ${deletionState} status ${status}`,
            });
        }
    }
}
function validateValueDispositionPolicy(policy, context) {
    const cases = new Map(policy.cases.map((row) => [row.caseId, row]));
    const unused = cases.get("unused_paid_value");
    if (!unused?.freezesValue ||
        !unused.requiredSelection.includes("refund_review") ||
        !unused.requiredSelection.includes("forfeiture_selection")) {
        context.addIssue({
            code: "custom",
            path: ["cases"],
            message: "Unused paid value must freeze and require refund_review or forfeiture_selection",
        });
    }
    const unresolved = cases.get("unresolved_refund_or_dispute");
    if (!unresolved?.blocksExecution || unresolved.disposition !== "manual_review_unresolved") {
        context.addIssue({
            code: "custom",
            path: ["cases"],
            message: "Unresolved refunds/disputes must block execution for manual review",
        });
    }
}
function validatePreflightBlockerCodes(registry, context) {
    const codes = new Set();
    for (const [index, row] of registry.codes.entries()) {
        if (codes.has(row.code)) {
            context.addIssue({
                code: "custom",
                path: ["codes", index, "code"],
                message: "Duplicate code",
            });
        }
        codes.add(row.code);
    }
    for (const required of REQUIRED_BLOCKER_CODES) {
        if (!codes.has(required)) {
            context.addIssue({ code: "custom", path: ["codes"], message: `Missing ${required}` });
        }
    }
    for (const code of ["unresolved_refund", "unresolved_dispute"]) {
        const row = registry.codes.find((candidate) => candidate.code === code);
        if (row?.blocksProfileDeactivation) {
            context.addIssue({
                code: "custom",
                path: ["codes"],
                message: `${code} must not block profile deactivation`,
            });
        }
    }
}
function validatePreviewCopyLocales(copy, context) {
    const locales = copy.locales.map((locale) => locale.locale);
    if (locales[0] === locales[1] || !locales.includes("id") || !locales.includes("en")) {
        context.addIssue({
            code: "custom",
            path: ["locales"],
            message: "Preview copy must include id and en",
        });
    }
    const keySets = copy.locales.map((locale) => new Set(locale.blockerMessages.map((message) => message.key)));
    const [first, second] = keySets;
    if (!first || !second)
        return;
    for (const key of first) {
        if (!second.has(key)) {
            context.addIssue({
                code: "custom",
                path: ["locales"],
                message: "Preview blocker message keys must match across locales",
            });
        }
    }
}
function validateDecisionLockBundle(bundle, context) {
    const artifacts = [
        bundle.retentionPolicy,
        bundle.deletionTransformMatrix,
        bundle.accountDeletionStateMachine,
        bundle.latePaymentDecisionTable,
        bundle.legalHoldPolicy,
        bundle.valueDispositionPolicy,
        bundle.preflightBlockerCodes,
        bundle.tombstoneIdentityPolicy,
        bundle.supportOperationsPolicy,
        bundle.bilingualDeletionPreviewCopy,
    ];
    for (const [index, artifact] of artifacts.entries()) {
        if (artifact.version !== bundle.version) {
            context.addIssue({ code: "custom", path: [index], message: "Artifact version mismatch" });
        }
        if (artifact.environment !== "staging" || artifact.approved || artifact.publishable) {
            context.addIssue({
                code: "custom",
                path: [index],
                message: "Decision lock cannot claim production approval or publishability",
            });
        }
    }
    const retentionCategories = new Set(bundle.retentionPolicy.categories.map((category) => category.id));
    for (const [rowIndex, row] of bundle.deletionTransformMatrix.rows.entries()) {
        if (row.retentionCategory && !retentionCategories.has(row.retentionCategory)) {
            context.addIssue({
                code: "custom",
                path: ["deletionTransformMatrix", "rows", rowIndex, "retentionCategory"],
                message: "Unknown retention category",
            });
        }
    }
    const blockerCodes = new Set(bundle.preflightBlockerCodes.codes.map((row) => row.code));
    for (const locale of bundle.bilingualDeletionPreviewCopy.locales) {
        const keys = new Set(locale.blockerMessages.map((message) => message.key));
        for (const code of blockerCodes) {
            if (!keys.has(code)) {
                context.addIssue({
                    code: "custom",
                    path: ["bilingualDeletionPreviewCopy", "locales", locale.locale],
                    message: `Missing preview copy for ${code}`,
                });
            }
        }
    }
}
//# sourceMappingURL=account-lifecycle-policy.js.map